import { useCallback, useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { InlineAudioPlayer } from "@/components/InlineAudioPlayer";
import {
  Brain, RefreshCw, Loader2, ChevronLeft, ChevronRight, PhoneCall, PhoneOff,
  UserCheck, CalendarCheck, ThumbsUp, AlertTriangle, Lightbulb, ArrowRight,
  Building2, User, Info, Quote, Headphones, ExternalLink,
} from "lucide-react";

const PERIODS = [
  { key: "day", label: "Jour" },
  { key: "week", label: "Semaine" },
  { key: "month", label: "Mois" },
];

// Palette par nature de frein (interne = fait du CDP ; externe = magasins).
const NATURE = {
  interne: { label: "Interne (CDP)", color: "var(--shariti-orange)", bg: "rgba(238,114,46,0.12)", Icon: User },
  externe: { label: "Externe (magasins)", color: "#2F6FED", bg: "rgba(47,111,237,0.12)", Icon: Building2 },
};

const CONFIANCE = {
  faible: { label: "Confiance faible", bg: "rgba(214,69,69,0.12)", fg: "#B23434" },
  moyenne: { label: "Confiance moyenne", bg: "rgba(240,190,20,0.2)", fg: "#5A4200" },
  "élevée": { label: "Confiance élevée", bg: "rgba(31,157,85,0.14)", fg: "#0F5A2F" },
};

const sentimentStyle = (s) => {
  const v = (s || "").toLowerCase();
  if (v.includes("posit")) return { background: "rgba(31,157,85,0.14)", color: "#0F5A2F" };
  if (v.includes("nég") || v.includes("neg")) return { background: "rgba(214,69,69,0.12)", color: "#B23434" };
  return { background: "var(--surface-2)", color: "#6B6B6B" };
};

function toISO(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}
function shiftDate(iso, period, dir) {
  const d = iso ? new Date(iso + "T00:00:00") : new Date();
  if (period === "day") d.setDate(d.getDate() + dir);
  else if (period === "month") d.setMonth(d.getMonth() + dir);
  else d.setDate(d.getDate() + dir * 7);
  return toISO(d);
}

// Libellés FR lisibles, calculés LOCALEMENT (mise à jour instantanée à la navigation).
const FR = {
  full: new Intl.DateTimeFormat("fr-FR", { weekday: "long", day: "numeric", month: "long", year: "numeric" }),
  dm: new Intl.DateTimeFormat("fr-FR", { day: "numeric", month: "short" }),
  dmy: new Intl.DateTimeFormat("fr-FR", { day: "numeric", month: "short", year: "numeric" }),
  my: new Intl.DateTimeFormat("fr-FR", { month: "long", year: "numeric" }),
};
const cap = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);
function mondayOf(d) {
  const x = new Date(d);
  const off = (x.getDay() + 6) % 7;   // lundi = 0
  x.setDate(x.getDate() - off);
  x.setHours(0, 0, 0, 0);
  return x;
}
// Aligné sur le backend : jour = date exacte, semaine = lundi→dimanche, mois = mois entier.
function readableLabel(iso, period) {
  const d = iso ? new Date(iso + "T00:00:00") : new Date();
  if (period === "day") return cap(FR.full.format(d));
  if (period === "month") return cap(FR.my.format(d));
  const mon = mondayOf(d);
  const sun = new Date(mon); sun.setDate(sun.getDate() + 6);
  const sameYear = mon.getFullYear() === sun.getFullYear();
  return `${sameYear ? FR.dm.format(mon) : FR.dmy.format(mon)} – ${FR.dmy.format(sun)}`;
}

/**
 * Diagnostic IA « Pourquoi ce CDP ne convertit pas ? » (admin).
 * Dialog réutilisable : sélecteur de période, navigation, bouton « Relancer ».
 */
export function CdpDiagnostic({ cdpId, cdpName, open, onOpenChange, initialPeriod = "week", initialDate = null }) {
  const [period, setPeriod] = useState(initialPeriod);
  const [refDate, setRefDate] = useState(initialDate || toISO(new Date()));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const reqSeq = useRef(0);

  const load = useCallback(async (force = false) => {
    if (!cdpId) return;
    const myId = ++reqSeq.current;            // garde anti-course : n'applique que la dernière requête
    setLoading(true);
    if (force) setData(null);
    try {
      const { data: d } = await api.get(`/performance/cdp/${cdpId}/diagnostic`, {
        params: { period, date: refDate, force },
      });
      if (myId === reqSeq.current) setData(d);
    } catch {
      if (myId === reqSeq.current) toast.error("Diagnostic indisponible pour le moment.");
    } finally {
      if (myId === reqSeq.current) setLoading(false);
    }
  }, [cdpId, period, refDate]);

  // Réinitialise la période/date sur les valeurs d'entrée à chaque ouverture.
  useEffect(() => {
    if (open) { setPeriod(initialPeriod); setRefDate(initialDate || toISO(new Date())); }
    // eslint-disable-next-line
  }, [open, cdpId]);

  // (Re)charge en DIFFÉRÉ (anti-rebond 350 ms) à chaque changement de période/date :
  // évite de lancer une analyse à chaque clic rapide sur les flèches et supprime les
  // « sauts de dates » dus aux réponses arrivant dans le désordre.
  useEffect(() => {
    if (!open || !cdpId) return;
    const t = setTimeout(() => load(false), 350);
    return () => clearTimeout(t);
  }, [open, cdpId, period, refDate, load]);

  const diag = data?.diagnostic;
  const stats = data?.stats;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[92vh] max-w-2xl overflow-y-auto" data-testid="cdp-diagnostic-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <span className="grid h-8 w-8 place-items-center rounded-lg bg-[rgba(238,114,46,0.14)] text-[var(--shariti-orange)]">
              <Brain className="h-4.5 w-4.5" />
            </span>
            Diagnostic IA — {cdpName || "CDP"}
          </DialogTitle>
          <DialogDescription>
            Pourquoi ce CDP ne décroche pas d'emplacements ? Synthèse des appels de la période.
          </DialogDescription>
        </DialogHeader>

        {/* Barre de contrôle : période (haut) + navigation date lisible (bas) */}
        <div className="rounded-xl border border-[var(--border-soft)] bg-[var(--surface-2)] p-2">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="flex items-center gap-1 rounded-lg border border-[var(--border-soft)] bg-white p-0.5" data-testid="diag-period-toggle">
              {PERIODS.map((p) => (
                <button key={p.key} onClick={() => setPeriod(p.key)} data-testid={`diag-period-${p.key}`}
                  className={`rounded-md px-2.5 py-1 text-xs font-bold transition-colors ${period === p.key
                    ? "bg-[var(--shariti-orange)] text-white" : "text-muted-ink hover:bg-[var(--surface-2)]"}`}>
                  {p.label}
                </button>
              ))}
            </div>
            <Button size="sm" onClick={() => load(true)} disabled={loading}
              className="bg-[var(--shariti-orange)] text-white hover:bg-[#E56622]" data-testid="diag-refresh">
              {loading ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <RefreshCw className="mr-1.5 h-4 w-4" />}
              Relancer l'analyse
            </Button>
          </div>
          <div className="mt-2 flex items-center justify-center gap-1">
            <Button variant="ghost" size="icon" className="h-8 w-8 shrink-0" onClick={() => setRefDate((d) => shiftDate(d, period, -1))} data-testid="diag-prev" aria-label="Période précédente"><ChevronLeft className="h-4 w-4" /></Button>
            <div className="flex min-w-[200px] items-center justify-center gap-1.5 px-2 text-center">
              {loading && <Loader2 className="h-3.5 w-3.5 shrink-0 animate-spin text-[var(--shariti-orange)]" />}
              <span className="truncate text-sm font-bold text-ink" data-testid="diag-period-label" title={readableLabel(refDate, period)}>{readableLabel(refDate, period)}</span>
            </div>
            <Button variant="ghost" size="icon" className="h-8 w-8 shrink-0" onClick={() => setRefDate((d) => shiftDate(d, period, 1))} data-testid="diag-next" aria-label="Période suivante"><ChevronRight className="h-4 w-4" /></Button>
            <Button variant="ghost" size="sm" className="h-8 shrink-0 px-2 text-xs font-semibold" onClick={() => setRefDate(toISO(new Date()))} data-testid="diag-today" title="Revenir à aujourd'hui">Aujourd'hui</Button>
          </div>
        </div>

        {data?.demo && (
          <div className="flex items-center gap-1.5 rounded-lg bg-[rgba(252,219,83,0.3)] px-3 py-1.5 text-[11px] font-semibold text-[#5A4200]" data-testid="diag-demo-badge">
            <Info className="h-3.5 w-3.5" /> Données de démonstration (pas assez d'appels réels sur la période).
          </div>
        )}

        {loading && !data ? (
          <div className="space-y-3" data-testid="diag-loading">
            <Skeleton className="h-20 w-full rounded-xl" />
            <Skeleton className="h-24 w-full rounded-xl" />
            <Skeleton className="h-32 w-full rounded-xl" />
          </div>
        ) : !diag ? (
          <div className="rounded-xl border border-dashed border-[var(--border-soft)] px-4 py-10 text-center text-sm text-muted-ink" data-testid="diag-empty">
            Aucun diagnostic disponible.
          </div>
        ) : (
          <div className={`space-y-4 transition-opacity ${loading ? "pointer-events-none opacity-50" : ""}`} data-testid="diag-content">
            {/* Verdict */}
            <div className="rounded-2xl border border-[rgba(238,114,46,0.4)] bg-[rgba(238,114,46,0.06)] p-4" data-testid="diag-verdict">
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-start gap-2">
                  <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-[var(--shariti-orange)]" />
                  <div>
                    <div className="text-[11px] font-bold uppercase tracking-wide text-[var(--shariti-orange)]">Verdict</div>
                    <p className="mt-0.5 text-sm font-extrabold leading-snug text-ink">{diag.verdict || "—"}</p>
                  </div>
                </div>
                {diag.confiance && (
                  <span className="shrink-0 rounded-full px-2 py-0.5 text-[10px] font-bold"
                    style={{ background: (CONFIANCE[diag.confiance] || CONFIANCE.moyenne).bg, color: (CONFIANCE[diag.confiance] || CONFIANCE.moyenne).fg }}
                    title={`Fiabilité estimée d'après le volume analysé : ${stats?.analyzed ?? 0} appel(s) analysé(s) sur ${stats?.calls ?? 0}`}
                    data-testid="diag-confiance">
                    {(CONFIANCE[diag.confiance] || CONFIANCE.moyenne).label}
                  </span>
                )}
              </div>
              {diag.synthese && <p className="mt-2.5 text-[13px] leading-relaxed text-ink">{diag.synthese}</p>}
            </div>

            {/* Stats clés */}
            {stats && (
              <div className="grid grid-cols-2 gap-2 sm:grid-cols-5" data-testid="diag-stats">
                <StatTile Icon={PhoneCall} label="Appels" value={stats.calls} />
                <StatTile Icon={UserCheck} label="Aboutis" value={`${stats.reach_rate}%`} sub={`${stats.reached}/${stats.outbound}`} tone="green" />
                <StatTile Icon={UserCheck} label="Décideur" value={`${stats.dec_rate}%`} sub={`${stats.decideurs}`} />
                <StatTile Icon={CalendarCheck} label="Dates" value={`${stats.date_rate}%`} sub={`${stats.dates}`} tone="orange" />
                <StatTile Icon={PhoneOff} label="Sans réponse" value={stats.no_answer} tone="danger" />
              </div>
            )}

            {/* Responsabilité interne / externe */}
            {diag.responsabilite && (
              <div className="rounded-2xl border border-[var(--border-soft)] bg-white p-4" data-testid="diag-responsabilite">
                <div className="mb-2 text-[11px] font-bold uppercase tracking-wide text-muted-ink">Origine du blocage</div>
                <div className="flex h-3 w-full overflow-hidden rounded-full bg-[var(--surface-2)]">
                  <div className="h-full transition-[width] duration-300" style={{ width: `${diag.responsabilite.interne_pct}%`, background: NATURE.interne.color }} data-testid="diag-resp-interne-bar" />
                  <div className="h-full transition-[width] duration-300" style={{ width: `${diag.responsabilite.externe_pct}%`, background: NATURE.externe.color }} data-testid="diag-resp-externe-bar" />
                </div>
                <div className="mt-2 flex flex-wrap items-center justify-between gap-2 text-xs">
                  <span className="inline-flex items-center gap-1 font-bold" style={{ color: NATURE.interne.color }}>
                    <User className="h-3.5 w-3.5" /> Interne (CDP) · {diag.responsabilite.interne_pct}%
                  </span>
                  <span className="inline-flex items-center gap-1 font-bold" style={{ color: NATURE.externe.color }}>
                    <Building2 className="h-3.5 w-3.5" /> Externe (magasins) · {diag.responsabilite.externe_pct}%
                  </span>
                </div>
                {diag.responsabilite.commentaire && <p className="mt-2 text-[12px] text-muted-ink">{diag.responsabilite.commentaire}</p>}
              </div>
            )}

            {/* Facteurs de blocage */}
            {diag.facteurs?.length > 0 && (
              <div data-testid="diag-facteurs">
                <div className="mb-2 text-[11px] font-bold uppercase tracking-wide text-muted-ink">Freins identifiés (par poids)</div>
                <div className="space-y-2.5">
                  {diag.facteurs.map((f, i) => {
                    const nat = NATURE[f.nature] || NATURE.externe;
                    return (
                      <div key={i} className="rounded-xl border border-[var(--border-soft)] bg-white p-3" data-testid={`diag-facteur-${i}`}>
                        <div className="flex items-center justify-between gap-2">
                          <span className="inline-flex items-center gap-1.5 text-sm font-bold text-ink">
                            <span className="inline-flex items-center gap-1 rounded-full px-1.5 py-0.5 text-[9px] font-extrabold uppercase tracking-wide" style={{ background: nat.bg, color: nat.color }}>
                              <nat.Icon className="h-2.5 w-2.5" />{f.nature === "interne" ? "CDP" : "Magasin"}
                            </span>
                            {f.label}
                          </span>
                          <span className="shrink-0 text-xs font-extrabold tabular-nums" style={{ color: nat.color }}>{f.poids}%</span>
                        </div>
                        <div className="mt-1.5 h-1.5 w-full overflow-hidden rounded-full bg-[var(--surface-2)]">
                          <div className="h-full rounded-full" style={{ width: `${f.poids}%`, background: nat.color }} />
                        </div>
                        {f.constat && <p className="mt-2 text-[12px] leading-relaxed text-ink">{f.constat}</p>}
                        {f.preuves?.length > 0 && (
                          <div className="mt-2 space-y-1">
                            {f.preuves.map((p, j) => (
                              <p key={j} className="border-l-2 pl-2 text-[11px] italic text-muted-ink" style={{ borderColor: nat.color }}>« {p} »</p>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Écoutes clés : accès direct aux 2-3 appels les plus parlants */}
            {data?.ecoutes?.length > 0 && (
              <div data-testid="diag-ecoutes">
                <div className="mb-2 flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wide text-muted-ink"><Headphones className="h-3.5 w-3.5 text-[var(--shariti-orange)]" />Écoutes clés</div>
                <div className="space-y-2">
                  {data.ecoutes.map((e, i) => (
                    <div key={e.call_id} className="rounded-xl border border-[var(--border-soft)] bg-white p-3" data-testid={`diag-ecoute-${i}`}>
                      <div className="flex items-center justify-between gap-2">
                        <span className="break-words text-sm font-bold text-ink">{e.contact}</span>
                        <span className="flex shrink-0 items-center gap-1">
                          {e.outcome && <span className="rounded-full bg-[var(--surface-2)] px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide text-muted-ink">{e.outcome}</span>}
                          {e.sentiment && <span className="rounded-full px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide" style={sentimentStyle(e.sentiment)}>{e.sentiment}</span>}
                        </span>
                      </div>
                      {e.verbatim && e.verbatim !== "—" && (
                        <p className="mt-1.5 border-l-2 border-[var(--shariti-orange)] pl-2 text-[12px] italic leading-relaxed text-muted-ink">« {e.verbatim} »</p>
                      )}
                      {e.has_recording ? (
                        <InlineAudioPlayer callId={e.call_id} totalSec={e.duration_sec} className="mt-2 max-w-[360px]" />
                      ) : (
                        <a href={`/appels?call=${e.call_id}`} target="_blank" rel="noopener noreferrer"
                          className="mt-2 inline-flex items-center gap-1 text-[11px] font-bold text-[var(--shariti-orange)] hover:underline"
                          data-testid={`diag-ecoute-open-${i}`}>
                          <ExternalLink className="h-3 w-3" />Ouvrir la fiche appel
                        </a>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Points forts / vigilance */}
            {(diag.points_forts?.length > 0 || diag.points_vigilance?.length > 0) && (
              <div className="grid gap-3 sm:grid-cols-2">
                {diag.points_forts?.length > 0 && (
                  <div className="rounded-xl border border-[rgba(31,157,85,0.3)] bg-[rgba(31,157,85,0.05)] p-3" data-testid="diag-forts">
                    <div className="mb-1.5 flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wide text-[#0F5A2F]"><ThumbsUp className="h-3.5 w-3.5" />Points forts</div>
                    <ul className="space-y-1">
                      {diag.points_forts.map((p, i) => (
                        <li key={i} className="flex items-start gap-1.5 text-[12px] text-ink"><span className="mt-1.5 h-1 w-1 shrink-0 rounded-full bg-[#1F9D55]" />{p}</li>
                      ))}
                    </ul>
                  </div>
                )}
                {diag.points_vigilance?.length > 0 && (
                  <div className="rounded-xl border border-[rgba(240,190,20,0.5)] bg-[rgba(252,219,83,0.15)] p-3" data-testid="diag-vigilance">
                    <div className="mb-1.5 flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wide text-[#5A4200]"><AlertTriangle className="h-3.5 w-3.5" />À travailler (côté CDP)</div>
                    <ul className="space-y-1">
                      {diag.points_vigilance.map((p, i) => (
                        <li key={i} className="flex items-start gap-1.5 text-[12px] text-ink"><span className="mt-1.5 h-1 w-1 shrink-0 rounded-full bg-[#B5470F]" />{p}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}

            {/* Recommandations */}
            {diag.recommandations?.length > 0 && (
              <div data-testid="diag-recos">
                <div className="mb-2 flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wide text-muted-ink"><Lightbulb className="h-3.5 w-3.5 text-[var(--shariti-orange)]" />Recommandations pour l'accompagner</div>
                <div className="space-y-2">
                  {diag.recommandations.map((r, i) => (
                    <div key={i} className="rounded-xl border border-[var(--border-soft)] bg-[rgba(238,114,46,0.03)] p-3" data-testid={`diag-reco-${i}`}>
                      <div className="flex items-center gap-2 text-sm font-bold text-ink">
                        <span className="grid h-5 w-5 shrink-0 place-items-center rounded-md bg-[var(--shariti-orange)] text-[10px] font-extrabold text-white">{i + 1}</span>
                        {r.titre}
                      </div>
                      {r.action && <p className="mt-1.5 flex items-start gap-1.5 text-[12px] font-semibold text-[#0F5A2F]"><ArrowRight className="mt-0.5 h-3.5 w-3.5 shrink-0" />{r.action}</p>}
                      {r.exemple && (
                        <div className="mt-2 flex items-start gap-1.5 rounded-lg bg-[var(--surface-2)] px-2 py-1.5 text-[11px] italic leading-relaxed text-muted-ink" data-testid={`diag-reco-exemple-${i}`}>
                          <Quote className="mt-0.5 h-3 w-3 shrink-0 text-[var(--shariti-orange)]" />
                          <span><span className="font-bold not-italic text-ink">Exemple : </span>{r.exemple}</span>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            <p className="pt-1 text-center text-[10px] text-muted-ink" data-testid="diag-footer">
              {data?.ai_used ? "Analyse générée par l'IA" : "Analyse automatique (repli)"} · {stats?.analyzed || 0} appel(s) analysé(s) sur {stats?.calls || 0}
            </p>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function StatTile({ Icon, label, value, sub, tone }) {
  const tones = {
    green: "text-[#0F5A2F]", orange: "text-[var(--shariti-orange)]",
    danger: "text-[#B23434]", default: "text-ink",
  };
  return (
    <div className="rounded-xl border border-[var(--border-soft)] bg-white px-2.5 py-2 text-center" data-testid={`diag-stat-${label}`}>
      <div className="flex items-center justify-center gap-1 text-[10px] font-semibold text-muted-ink"><Icon className="h-3 w-3" />{label}</div>
      <div className={`mt-0.5 text-lg font-extrabold leading-none ${tones[tone] || tones.default}`}>{value}</div>
      {sub && <div className="mt-0.5 text-[10px] text-muted-ink">{sub}</div>}
    </div>
  );
}

export default CdpDiagnostic;
